// components/feed/MobileFeedCarousel.js - UPDATED WITH COMMITTEE FEED SUPPORT AND THEME SUPPORT
// Only changes: Added CommitteeCarouselCard component and isCommitteeFeed detection
import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { Link, useNavigate } from 'react-router-dom';
import useFeedData from '../../hooks/useFeedData';
import useCommitteeFeedData from '../../hooks/useCommitteeFeedData'; // NEW IMPORT
import FeedItem from './FeedItem';
import { generateFeedUrlFromApiEndpoint } from '../../utils/feedUrlUtils';
import { isCommitteeFeed, generateCommitteeFeedUrl } from '../../utils/committeeFeedHelpers'; // NEW IMPORT
import { isCrawlFeed } from '../../utils/crawlFeedAdapter';
import MemberCarouselCard from '../members/MemberCarouselCard';
import { MEMBER_TYPES } from '../../utils/membersConfig';

// Optional: Import Capacitor modules only if available
let Haptics, ImpactStyle;
try {
  ({ Haptics, ImpactStyle } = require('@capacitor/haptics'));
} catch (error) {
  // Capacitor modules not available, use fallbacks
  // console.log('Capacitor haptics not available, using fallbacks');
}

// Crawl-backed feeds (crawl=recent) get no per-feed heading. Two reasons:
//
//   1. On StatePage the Updates branch holds a single feed whose name matches
//      the branch label, so the heading printed here repeats the branch header
//      immediately above it — "Web Crawl Updates" twice, stacked.
//   2. The heading is a link, and there is nowhere for it to go. Crawl feeds
//      carry no schema/table, so generateFeedUrlFromApiEndpoint fails and the
//      fallback below lands on /feed/united_states_of_america/default.
//
// isCrawlFeed expects a bare param string, so strip any leading & or ? first —
// apiParams arrives with and without one depending on the caller.
const isCrawlBackedFeed = (apiParams) => isCrawlFeed((apiParams || '').replace(/^[&?]+/, ''));

const handleHapticsClick = async (callback) => {
  window.scrollTo(0, 0);
  if (Haptics && ImpactStyle) {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      // Haptics not supported, just continue
    }
  }
  callback();
};

const MobileFeedCarousel = ({ feeds }) => {
  const navigate = useNavigate();
  const [modalState, setModalState] = useState({});
  const [visibleCards, setVisibleCards] = useState([0]);
  const scrollAreaRefs = useRef({});

  const closeModal = (id) => setModalState({ ...modalState, [id]: false });

  // Enhanced function to handle "View More" click - works for both regular feeds and agency navigation
  // UPDATED: Now also handles committee feeds
  const handleFeedClick = (apiParams, feedName, feedBranch, feedTopic, feedButtonName, agencyRoute, departmentPage, feed) => {
    // Crawl feeds carry no schema/table, so the URL generators below have
    // nothing to build from and would land on /feed/{schema}/default. They
    // instead go wherever the caller pointed them: StatePage supplies a
    // viewAllLink back into its own Updates view. No link means no navigation.
    if (feed && isCrawlBackedFeed(feed.apiParams)) {
      if (feed.viewAllLink) {
        window.scrollTo(0, 0);
        navigate(feed.viewAllLink);
      }
      return;
    }

    // NEW: Check if this is a committee feed
    if (feed && isCommitteeFeed(feed)) {
      const url = generateCommitteeFeedUrl(apiParams);
      // console.log('Committee feed - navigating to:', url);
      window.scrollTo(0, 0);
      navigate(url);
      return;
    }
    
    // If this is from a department page and has an agencyRoute, navigate to agency page
    if (departmentPage && agencyRoute) {
      // console.log('Department page - navigating to agency:', agencyRoute);
      window.scrollTo(0, 0);
      navigate(agencyRoute);
      return;
    }
    
    // Use enhanced URL generation for better handling of complex parameters
    const urlData = generateFeedUrlFromApiEndpoint(apiParams);
    
    if (urlData) {
      // console.log('Enhanced URL generation - navigating to:', urlData.fullUrl);
      window.scrollTo(0, 0);
      navigate(urlData.fullUrl);
    } else {
      // Fallback to old method if enhanced generation fails
      console.warn('Using fallback URL generation for:', apiParams);
      let cleanParams = apiParams;
      if (cleanParams.startsWith('&') || cleanParams.startsWith('?')) {
        cleanParams = cleanParams.substring(1);
      }
      
      const urlParams = new URLSearchParams(cleanParams);
      const schema = urlParams.get('schema') || 'united_states_of_america';
      const table = urlParams.get('table') || urlParams.get('table_name') || 'default';
      
      // console.log('Fallback - navigating to:', `/feed/${schema}/${table}`);
      window.scrollTo(0, 0);
      navigate(`/feed/${schema}/${table}`);
    }
  };

  // Modified function to create cards from normal feed with proper skip handling
  // UPDATED: Now marks committee feeds
  const createCardsFromNormalFeed = (feed) => {
    const cards = [];
    const isCommittee = isCommitteeFeed(feed); // NEW: Detect committee feeds
    
    // Create 4 cards with incremented skip values
    for (let i = 0; i < 4; i++) {
      const cardId = `${feed.id}-card-${i}`;
      
      // Create a copy of the feed for this card
      const cardFeed = {
        ...feed,
        id: cardId,
        skip: i, // Add skip as a direct property
        isCommitteeFeed: isCommittee, // NEW: Pass this flag to the card
      };
      
      // Update apiParams to include skip
      if (cardFeed.apiParams.includes('skip=')) {
        // Replace existing skip parameter
        cardFeed.apiParams = cardFeed.apiParams.replace(/skip=\d+/, `skip=${i}`);
      } else {
        // Add skip parameter if it doesn't exist
        cardFeed.apiParams += `&skip=${i}`;
      }
      
      cards.push(cardFeed);
    }
    
    return cards;
  };

  // Group carousel feeds by their group property
  const groupedFeeds = feeds
    .filter(feed => feed.displayType === 'carousel')
    .reduce((acc, feed) => {
      if (feed.group) {
        if (!acc[feed.group]) {
          acc[feed.group] = {
            displayName: feed.groupDisplayName,
            link: feed.groupLink,
            showGroupButton: feed.showGroupButton || false,
            groupButtonText: feed.groupButtonText || '',
            items: []
          };
        }
        acc[feed.group].items.push(feed);
      }
      return acc;
    }, {});
  
  // Create normal feeds as custom carousels
  const normalFeeds = feeds.filter(feed => feed.displayType === 'normal');

  // Setup intersection observer for lazy loading
  useEffect(() => {
    const normalFeedsLength = normalFeeds.length;
    const groupedFeedsLength = Object.keys(groupedFeeds).length;
    
    // Create a map to store observers
    const observers = {};
    
    // For each carousel group, create an observer
    Object.keys(scrollAreaRefs.current).forEach(groupId => {
      const scrollArea = scrollAreaRefs.current[groupId];
      if (!scrollArea) return;
      
      const options = {
        root: scrollArea,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Extract group and card index
            const cardId = entry.target.dataset.cardId;
            if (cardId) {
              // Update visible cards state
              setVisibleCards(prev => {
                if (!prev.includes(cardId)) {
                  return [...prev, cardId];
                }
                return prev;
              });
            }
          }
        });
      }, options);
      
      observers[groupId] = observer;
      
      // Find all card wrappers in this scroll area
      const cards = scrollArea.querySelectorAll('.card-wrapper');
      cards.forEach((card, index) => {
        // Set a unique identifier for this card
        card.dataset.cardId = `${groupId}-${index}`;
        observer.observe(card);
      });
    });
    
    // Cleanup function to disconnect all observers
    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, [normalFeeds.length, Object.keys(groupedFeeds).length]); // Run when feeds change

  // Helper function to check if a card is visible
  const isCardVisible = (groupId, cardIndex) => {
    const cardKey = `${groupId}-${cardIndex}`;
    return visibleCards.includes(cardKey) || visibleCards.includes(0); // First card is always visible
  };

  // Helper function to get member type from string
  const getMemberType = (memberTypeString) => {
    switch (memberTypeString) {
      case 'senate':
        return MEMBER_TYPES.SENATE;
      case 'house':
        return MEMBER_TYPES.HOUSE;
      case 'president':
        return MEMBER_TYPES.PRESIDENT;
      case 'justice':
        return MEMBER_TYPES.JUSTICE;
      default:
        return MEMBER_TYPES.HOUSE;
    }
  };

  return (
   <>
      {/* Render normal feeds as custom carousels */}
      {normalFeeds.map((feed) => {
        const cardsForFeed = createCardsFromNormalFeed(feed);
        const groupId = `normal-${feed.id}`;
        const isCommittee = isCommitteeFeed(feed); // NEW: Detect committee feeds
        const isCrawl = isCrawlBackedFeed(feed.apiParams);
        
        return (
          <div key={`${feed.id}-group`} className="carouselGroupContainer news-section">
            {/* Heading that triggers the appropriate click handler. Crawl feeds
                are excluded: see isCrawlBackedFeed above. */}
            {feed.name && !isCrawl && (
              <div 
                onClick={() => handleHapticsClick(() => handleFeedClick(
                  feed.apiParams,
                  feed.name,
                  feed.feedBranch,
                  feed.feedTopic,
                  feed.buttonName,
                  feed.agencyRoute,
                  feed.departmentPage,
                  feed // NEW: Pass feed object for committee detection
                ))}
                id="pageLink"
                className="carouselGroupTitleLink"
                style={{ cursor: 'pointer' }}
              >
                <h5 className="carousel-heading">
                  <div className="title-container">
                    <span className="heading-text">{feed.name}</span>
                    <ChevronRight id='clickChevron' className="heading-chevron" />
                  </div>
                </h5>
              </div>
            )}

            <div className="scroll-container">
              <div className="gradient-left" />
              <div className="gradient-right" />
              <div 
                className="scroll-area"
                ref={el => scrollAreaRefs.current[groupId] = el}
              >
                <div className="card-container">
                  {cardsForFeed.map((cardFeed, cardIndex) => (
                    // NEW: Conditionally render CommitteeCarouselCard or MobileCarouselCard
                    isCommittee ? (
                      <CommitteeCarouselCard
                        key={cardFeed.id}
                        feed={cardFeed}
                        isVisible={isCardVisible(groupId, cardIndex)}
                      />
                    ) : (
                      <MobileCarouselCard
                        key={cardFeed.id}
                        feed={cardFeed}
                        isVisible={isCardVisible(groupId, cardIndex)}
                      />
                    )
                  ))}

                  {/* View All button. A crawl feed only gets one when the
                      caller gave it a viewAllLink to go to. */}
                  {(!isCrawl || feed.viewAllLink) && (
                    <div className="card-wrapper view-all-card">
                      <div id="viewFullPageButton">
                        <Button 
                          id="viewFullLegislativePageButtonMobile" 
                          size="lg"
                          onClick={() => handleHapticsClick(() => handleFeedClick(
                            feed.apiParams,
                            feed.name,
                            feed.feedBranch,
                            feed.feedTopic,
                            feed.buttonName,
                            feed.agencyRoute,
                            feed.departmentPage,
                            feed // NEW: Pass feed object for committee detection
                          ))}
                        >
                          {feed.departmentPage && feed.agencyRoute 
                            ? `View ${feed.name} page`
                            : `View all ${feed.name}`
                          }
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Render original carousel feeds with lazy loading */}
      {Object.entries(groupedFeeds).map(([groupId, group]) => (
        <React.Fragment key={groupId}>
          <div id={`carousel-${groupId}`} className="carouselGroupContainer news-section">
            {/* Only render the heading Link if groupDisplayName exists */}
            {group.displayName && (
              <Link
                onClick={() => handleHapticsClick(() => {})}
                id="pageLink"
                to={group.link}
                className="carouselGroupTitleLink"
              >
                <h5 className="carousel-heading">
                  <div className="title-container">
                    <span className="heading-text">{group.displayName}</span>
                    <ChevronRight id='clickChevron' className="heading-chevron" />
                  </div>
                </h5>
              </Link>
            )}
  
            <div className="scroll-container">
              <div className="gradient-left" />
              <div className="gradient-right" />
              <div 
                className="scroll-area"
                ref={el => scrollAreaRefs.current[groupId] = el}
              >
                <div className="card-container">
                  {group.items.map((feed, cardIndex) => {
                    const isCommittee = isCommitteeFeed(feed); // NEW: Detect committee feeds
                    
                    return (
                      <div 
                        key={feed.id} 
                        className="card-wrapper"
                      >
                        {/* Render heading for non-member items. Crawl feeds are
                            skipped here for the same reason as above. */}
                        {feed.name && feed.type !== 'member' && !isCrawlBackedFeed(feed.apiParams) && (
                          <div className="row mobile-feed-heading-row">
                            <h5 
                              className="feed-heading-mobile"
                              onClick={() => handleFeedClick(
                                feed.apiParams,
                                feed.name,
                                feed.feedBranch,
                                feed.feedTopic,
                                feed.buttonName,
                                undefined,
                                undefined,
                                feed // NEW: Pass feed object for committee detection
                              )}
                            >    
                              <div className="title-container">
                                <span className="heading-text">{feed.name}</span>
                                <ChevronRight id='clickChevron' className="heading-chevron" />
                              </div>
                            </h5>
                          </div>
                        )}
                        
                        <div className="card-content">
                          <div id={feed.id} className="courtNewsMobile">
                            <div id={`feed-${feed.id}`}>
                              {/* Render MemberCarouselCard for member type items */}
                              {feed.type === 'member' ? (
                                <MemberCarouselCard 
                                  memberType={getMemberType(feed.memberType)}
                                />
                              ) : isCommittee ? (
                                // NEW: Render CommitteeCarouselCard for committee feeds
                                <CommitteeCarouselCard
                                  feed={feed}
                                  isVisible={isCardVisible(groupId, cardIndex)}
                                />
                              ) : (
                                <MobileCarouselCard
                                  feed={feed}
                                  isVisible={isCardVisible(groupId, cardIndex)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
  
                  {/* Only show View All button if showGroupButton is true */}
                  {group.showGroupButton && (
                    <div className="card-wrapper view-all-card" >
                      <div id="viewFullPageButton">
                        <Link id='pageLink'
                          to={group.link}
                          onClick={() => handleHapticsClick(() => {})}
                        >
                          <Button 
                            id="viewFullLegislativePageButtonMobile" 
                            size="lg"
                          >
                            {group.groupButtonText || `View all ${group.displayName} news`}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
  
            {/* Modal for each feed */}
            {group.items.map((feed) => (
              modalState[feed.id] && (
                <div 
                  key={`modal-${feed.id}`} 
                  id={`fullBillModal-${feed.id}`}
                  className="modal-overlay" 
                  onClick={() => closeModal(feed.id)}
                >
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{feed.modalTitle}</h3>
                      <button className="close-button" onClick={() => closeModal(feed.id)}>×</button>
                    </div>
                    <div className="modal-body">
                      {feed.generateFunction && (
                        <feed.generateFunction
                          url={feed.apiParams}
                          name={feed.name}
                          branch={feed.feedBranch}
                          topic={feed.feedTopic}
                          buttonName={feed.buttonName}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
          {/* Add a spacer after each carousel */}
          <div className="carousel-spacer">
            <div id='carouSpacer'></div>
          </div>
        </React.Fragment>
      ))}
  
  <style>{`
       /* Base styling */
       /* Mobile carousel styles with guaranteed chevron visibility */
       .news-section {
        margin-bottom: 2rem;
      }
      
      /* Carousel heading with chevron appearing right after text */
      .carousel-heading {
        position: relative;
        padding: 16px 30px 8px 20px;
        margin: 0;
        color: var(--text-primary, white);
        height: 50px;
        overflow: hidden;
        display: flex;
        align-items: center;
      }
      
      /* Title container with proper flex layout */
      .title-container {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        min-width: 0;
      }
      
      /* Heading text that shrinks but leaves space for chevron */
      .heading-text {
        font-family: var(--site-font);
        font-size: clamp(15px, 4vw, 18px);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: calc(100% - 24px);
        margin-right: 4px;
        color: var(--text-primary, white);
      }
      
      .heading-text2 {
        font-family: var(--site-font);
        font-size: clamp(12px, 4vw, 16px);
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: calc(100% - 24px);
        margin-right: 4px;
        color: var(--text-primary, white);
      }
      
      /* Chevron that appears right after text */
      .heading-chevron {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        min-width: 16px;
        display: inline-block;
        color: var(--text-primary, white);
      }
      
      /* Mobile feed heading with same treatment */
      .mobile-feed-heading-row {
        height: 50px;
        overflow: hidden;
        margin: 0;
        padding: 0;
        background: transparent !important;
        box-shadow: none !important;
      }
      
      .feed-heading-mobile {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        padding: 8px 30px 8px 15px;
        margin: 0;
        color: var(--text-primary, white);
        cursor: pointer;
        height: 50px;
        overflow: hidden;
      }
      
      /* Feed heading mobile title container */
      .feed-heading-mobile .title-container {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        min-width: 0;
      }
      
      .feed-heading-mobile .heading-text {
        max-width: calc(100% - 24px);
        min-width: 0;
        margin-right: 4px;
      }
      
      .feed-heading-mobile .heading-chevron {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        min-width: 16px;
        display: inline-block;
      }
      
      /* Style for icons in headings */
      .title-container svg,
      .title-container img,
      #homeStateIcon {
        vertical-align: middle;
        height: 20px;
        width: auto;
        margin-right: 6px;
        display: inline-block;
      }
      
      /* Container styling */
      #legislativeConMobileNew {
        position: relative;
        margin-top: 0.5em;
        margin-bottom: 1em;
        padding-top: 10px;
        padding-bottom: 20px;
        border-radius: 20px;
        height: 300px;
        width: auto;
        overflow: hidden;
      }
      
      /* Scroll container */
      .scroll-container {
        position: relative;
        height: 290px;
      }
      
      .gradient-left, .gradient-right {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 48px;
        z-index: 5;
        pointer-events: none;
      }
      
      .gradient-left {
        left: 0;
      }
      
      .gradient-right {
        right: 0;
      }
      
      .scroll-area {
        box-sizing: border-box;
        height: 290px;
        overflow-x: scroll;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;   /* match HomePage */
        scrollbar-width: none;
        -ms-overflow-style: none;
        padding-bottom: 17px;
      }
      
      .scroll-area::-webkit-scrollbar {
        display: none;
      }
      
      /* Card layout */
      .card-container {
        display: flex;
        padding-left: 10px;
        padding-right: 10px;
        height: 280px;
      }
      
      .card-wrapper {
        flex: 0 0 430px !important;
        padding-right: 1rem;
        scroll-snap-align: center;
        box-sizing: border-box;
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
      }
      
      /* High specificity overrides to beat theme.css */
      .carouselGroupContainer .card-wrapper,
      .news-section .card-wrapper,
      .scroll-area .card-wrapper,
      .card-container .card-wrapper,
      .card-container > .card-wrapper,
      div.card-wrapper {
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        background-color: transparent !important;
      }
      
      .card-content {
        height: 160px;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        overflow: hidden;
        background: transparent !important;
        box-shadow: none !important;
      }
      
      .courtNewsMobile {
        background: transparent !important;
        box-shadow: none !important;
      }
      
      .courtNewsMobile > div,
      [id^="feed-"] {
        background: transparent !important;
        box-shadow: none !important;
      }
      
      .card-wrapper:first-child {
        padding-left: 1rem;
      }
      
      #feedContainerNormalView > div:last-child {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: center;
      }
      
      #viewFullLegislativePageButtonMobile {
        border: none;
        color: var(--text-primary, white);
        width: calc(100%);
        text-align: center;
        position: relative;
        background-color: var(--bg-button, rgb(1, 56, 90));
      }
      
      .close-button {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: var(--text-primary, white);
        cursor: pointer;
      }
      
      /* Remove text decorations from links */
      #pageLink,
      .carouselGroupTitleLink {
        text-decoration: none !important;
        color: inherit !important;
      }
      
      #pageLink:hover,
      .carouselGroupTitleLink:hover {
        text-decoration: none !important;
        color: inherit !important;
      }
      
      .carouselGroupTitleLink a {
        text-decoration: none !important;
        color: inherit !important;
      }
      
      .carouselGroupTitleLink a:hover {
        text-decoration: none !important;
        color: inherit !important;
      }
      
      .carousel-heading a,
      .carousel-heading a:hover,
      .carousel-heading a:focus,
      .carousel-heading a:active {
        text-decoration: none !important;
        color: var(--text-primary, white) !important;
      }
      
      /* Loading/Error/Empty state containers - FIXED */
      .card-loading-container,
      .mobile-card-loading,
      .mobile-card-error,
      .mobile-card-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 160px;
        width: 100%;
        border-radius: 8px;
        background: transparent !important;
        box-shadow: none !important;
      }
      
      /* Lazy loading placeholder */
      .lazy-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        background: rgba(255, 255, 255, 0.05);
        color: rgba(1, 56, 90, 0.24);
        font-size: 14px;
        border-radius: 8px;
      }
      
      /* Responsive adjustments */
      @media screen and (min-width: 768px) {
        .card-wrapper {
          flex: 0 0 320px;
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
        }
        
        .heading-text {
          font-size: 18px;
        }
      }
      
      @media screen and (max-width: 350px) {
        .heading-text {
          font-size: 13px;
        }
        
        .carousel-heading {
          padding-right: 25px;
        }
        
        .feed-heading-mobile {
          padding-right: 25px;
        }
      }
      
      /* Container height adjustments */
      .carouselGroupContainer:last-child,
      .news-section:last-child {
        /* Any specific styling for last items */
      }
      
      .carouselGroupContainer {
        height: 285px;
      }
      
      [id^="carousel-recents_canada"] {
        height: 220px;
      }
      
      [id^="carousel-recents_mexico"] {
        height: 220px;
      }
      
      [id^="carousel-recents_uk"] {
        height: 220px;
      }
      
      [id^="carousel-recents_all"] {
        height: 190px;
      }
      
      [id^="carousel-recents_defense"] {
        height: 190px;
      }
      
      [id^="carousel-recents_environment"] {
        height: 190px;
      }
      
      [id^="carousel-recents_health"] {
        height: 190px;
      }
      
      [id^="carousel-recents_foreign_affairs"] {
        height: 190px;
      }
       
       /* Additional responsiveness for very small screens */
       @media screen and (max-width: 350px) {
         .heading-text {
           font-size: 13px;
         }
       }

       /* ===========================================
          LIGHT THEME OVERRIDES
          =========================================== */

       [data-theme="light"] .carousel-heading,
       [data-theme="light"] .carousel-heading .heading-text,
       [data-theme="light"] .carousel-heading .heading-text2 {
         color: #000000 !important;
       }

       [data-theme="light"] .feed-heading-mobile,
       [data-theme="light"] .feed-heading-mobile .heading-text,
       [data-theme="light"] .feed-heading-mobile .heading-text2,
       [data-theme="light"] .feed-heading-mobile .title-container {
         color: #000000 !important;
       }

       [data-theme="light"] .heading-chevron {
         color: #000000 !important;
       }

       [data-theme="light"] #viewFullLegislativePageButtonMobile {
         background-color: #e9ecef;
         color: #000000 !important;
       }

       [data-theme="light"] .mobile-card-empty p,
       [data-theme="light"] .mobile-card-error p {
         color: #333333 !important;
       }

       [data-theme="light"] .lazy-placeholder {
         background: rgba(0, 0, 0, 0.05);
         color: #666666;
       }

       [data-theme="light"] .close-button {
         color: #000000;
       }

       [data-theme="light"] .carousel-heading a,
       [data-theme="light"] .carousel-heading a:hover,
       [data-theme="light"] .carousel-heading a:focus,
       [data-theme="light"] .carousel-heading a:active {
         color: #000000 !important;
       }

       [data-theme="light"] .card-wrapper,
       [data-theme="light"] .card-content,
       [data-theme="light"] .courtNewsMobile,
       [data-theme="light"] .carouselGroupContainer .card-wrapper,
       [data-theme="light"] .card-container .card-wrapper {
         background: transparent !important;
         background-color: transparent !important;
         box-shadow: none !important;
         border: none !important;
       }

       /* ===========================================
          DARK THEME OVERRIDES (True Black/OLED)
          =========================================== */

       [data-theme="dark"] .carousel-heading,
       [data-theme="dark"] .carousel-heading .heading-text,
       [data-theme="dark"] .carousel-heading .heading-text2 {
         color: #ffffff !important;
       }

       [data-theme="dark"] .feed-heading-mobile,
       [data-theme="dark"] .feed-heading-mobile .heading-text,
       [data-theme="dark"] .feed-heading-mobile .heading-text2,
       [data-theme="dark"] .feed-heading-mobile .title-container {
         color: #ffffff !important;
       }

       [data-theme="dark"] .heading-chevron {
         color: #ffffff !important;
       }

       [data-theme="dark"] #viewFullLegislativePageButtonMobile {
         background-color: #0a0a0a;
         color: #ffffff !important;
         border: 1px solid rgba(255, 255, 255, 0.08);
       }

       [data-theme="dark"] .lazy-placeholder {
         background: rgba(255, 255, 255, 0.03);
         color: rgba(255, 255, 255, 0.3);
       }

       [data-theme="dark"] .close-button {
         color: #ffffff;
       }

       [data-theme="dark"] .card-wrapper,
       [data-theme="dark"] .card-content,
       [data-theme="dark"] .courtNewsMobile,
       [data-theme="dark"] .carouselGroupContainer .card-wrapper,
       [data-theme="dark"] .card-container .card-wrapper {
         background: transparent !important;
         background-color: transparent !important;
         box-shadow: none !important;
         border: none !important;
       }

       /* ===========================================
          DAYLIGHT THEME OVERRIDES (Grey/Orange)
          =========================================== */

       [data-theme="daylight"] .carousel-heading,
       [data-theme="daylight"] .carousel-heading .heading-text,
       [data-theme="daylight"] .carousel-heading .heading-text2 {
         color: #1a1a1a !important;
       }

       [data-theme="daylight"] .feed-heading-mobile,
       [data-theme="daylight"] .feed-heading-mobile .heading-text,
       [data-theme="daylight"] .feed-heading-mobile .heading-text2,
       [data-theme="daylight"] .feed-heading-mobile .title-container {
         color: #1a1a1a !important;
       }

       [data-theme="daylight"] .heading-chevron {
         color: #1a1a1a !important;
       }

       [data-theme="daylight"] #viewFullLegislativePageButtonMobile {
         background-color: #f97316;
         color: #ffffff !important;
         border: none;
       }

       [data-theme="daylight"] #viewFullLegislativePageButtonMobile:hover {
         background-color: #ea580c;
       }

       [data-theme="daylight"] .mobile-card-empty p,
       [data-theme="daylight"] .mobile-card-error p {
         color: #333333 !important;
       }

       [data-theme="daylight"] .lazy-placeholder {
         background: rgba(0, 0, 0, 0.03);
         color: #888888;
       }

       [data-theme="daylight"] .close-button {
         color: #1a1a1a;
       }

       [data-theme="daylight"] .carousel-heading a,
       [data-theme="daylight"] .carousel-heading a:hover,
       [data-theme="daylight"] .carousel-heading a:focus,
       [data-theme="daylight"] .carousel-heading a:active {
         color: #1a1a1a !important;
       }

       [data-theme="daylight"] .card-wrapper,
       [data-theme="daylight"] .card-content,
       [data-theme="daylight"] .courtNewsMobile,
       [data-theme="daylight"] .carouselGroupContainer .card-wrapper,
       [data-theme="daylight"] .card-container .card-wrapper,
       [data-theme="daylight"] .mobile-feed-heading-row {
         background: transparent !important;
         background-color: transparent !important;
         box-shadow: none !important;
         border: none !important;
       }

       [data-theme="daylight"] .card-loading-container,
       [data-theme="daylight"] .mobile-card-loading,
       [data-theme="daylight"] .mobile-card-error,
       [data-theme="daylight"] .mobile-card-empty {
         background: transparent !important;
         box-shadow: none !important;
       }

       /* ===========================================
   COMFORT LIGHT THEME OVERRIDES
   =========================================== */

[data-mode="light"] .carousel-heading,
[data-mode="light"] .carousel-heading .heading-text,
[data-mode="light"] .carousel-heading .heading-text2 {
  color: var(--text-primary) !important;
}

[data-mode="light"] .feed-heading-mobile,
[data-mode="light"] .feed-heading-mobile .heading-text,
[data-mode="light"] .feed-heading-mobile .heading-text2,
[data-mode="light"] .feed-heading-mobile .title-container {
  color: var(--text-primary) !important;
}

[data-mode="light"] .heading-chevron {
  color: var(--text-primary) !important;
}

[data-mode="light"] #viewFullLegislativePageButtonMobile {
  background-color: var(--bg-card);
  color: var(--text-primary) !important;
  border: 1px solid var(--border-primary);
  box-shadow: none;
}

[data-mode="light"] .mobile-card-empty p,
[data-mode="light"] .mobile-card-error p {
  color: #4A4A4A !important;
}

[data-mode="light"] .lazy-placeholder {
  background: rgba(0, 0, 0, 0.03);
  color: #8B8B8B;
}

[data-mode="light"] .close-button {
  color: var(--text-primary);
}

[data-mode="light"] .carousel-heading a,
[data-mode="light"] .carousel-heading a:hover,
[data-mode="light"] .carousel-heading a:focus,
[data-mode="light"] .carousel-heading a:active {
  color: var(--text-primary) !important;
}

[data-mode="light"] .card-wrapper,
[data-mode="light"] .card-content,
[data-mode="light"] .courtNewsMobile,
[data-mode="light"] .carouselGroupContainer .card-wrapper,
[data-mode="light"] .card-container .card-wrapper,
[data-mode="light"] .mobile-feed-heading-row {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

[data-mode="light"] .card-loading-container,
[data-mode="light"] .mobile-card-loading,
[data-mode="light"] .mobile-card-error,
[data-mode="light"] .mobile-card-empty {
  background: transparent !important;
  box-shadow: none !important;
}

/* ===========================================
   COMFORT DARK THEME OVERRIDES
   =========================================== */

[data-mode="dark"] .carousel-heading,
[data-mode="dark"] .carousel-heading .heading-text,
[data-mode="dark"] .carousel-heading .heading-text2 {
  color: #E8E8E8 !important;
}

[data-mode="dark"] .feed-heading-mobile,
[data-mode="dark"] .feed-heading-mobile .heading-text,
[data-mode="dark"] .feed-heading-mobile .heading-text2,
[data-mode="dark"] .feed-heading-mobile .title-container {
  color: #E8E8E8 !important;
}

[data-mode="dark"] .heading-chevron {
  color: #E8E8E8 !important;
}

[data-mode="dark"] #viewFullLegislativePageButtonMobile {
  background-color: #1E1E1E;
  color: #E8E8E8 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

[data-mode="dark"] .lazy-placeholder {
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.3);
}

[data-mode="dark"] .close-button {
  color: #E8E8E8;
}

[data-mode="dark"] .card-wrapper,
[data-mode="dark"] .card-content,
[data-mode="dark"] .courtNewsMobile,
[data-mode="dark"] .carouselGroupContainer .card-wrapper,
[data-mode="dark"] .card-container .card-wrapper,
[data-mode="dark"] .mobile-feed-heading-row {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

[data-mode="dark"] .card-loading-container,
[data-mode="dark"] .mobile-card-loading,
[data-mode="dark"] .mobile-card-error,
[data-mode="dark"] .mobile-card-empty {
  background: transparent !important;
  box-shadow: none !important;
}
      `}</style>
      </>
    );
  };

// Individual Card Component that uses your existing FeedItem and useFeedData
// UNCHANGED from original
const MobileCarouselCard = ({ feed, isVisible }) => {
  // Use your existing useFeedData hook to fetch data for this specific feed
  const { data, loading, error } = useFeedData(feed.apiParams, 1);

  // Only render content if the card is visible - FIXED: wrapped in centered container
  if (!isVisible) {
    return (
      <div className="card-wrapper">
        <div className="card-loading-container">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Show loading state - FIXED: using proper class with CSS definition
  if (loading) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-loading">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Show error state - FIXED: removed <p> wrapper around spinner
  if (error) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-error">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-empty">
          <p style={{ color: 'var(--text-secondary, white)', fontSize: '14px', textAlign: 'center', margin: 0 }}>
            No content available
          </p>
        </div>
      </div>
    );
  }

  // Extract schema and table for FeedItem
  const urlParams = new URLSearchParams(feed.apiParams.replace(/^[&?]/, ''));
  const schema = urlParams.get('schema') || 'united_states_of_america';
  const table = urlParams.get('table') || urlParams.get('table_name') || 'default';

  // Use your existing FeedItem component
  return (
    <div className="card-wrapper">
      <div className="card-content" style={{ height: '160px', overflow: 'hidden' }}>
        <FeedItem
          item={data[0]}
          feedName={feed.name}
          branch={feed.feedBranch}
          topic={feed.feedTopic}
          buttonName={feed.buttonName}
          schema={schema}
          table={table}
          expanded={false}
          variant="homepage"
        />
      </div>
    </div>
  );
};

// NEW: Committee Card Component that uses useCommitteeFeedData hook
const CommitteeCarouselCard = ({ feed, isVisible }) => {
  // Use the committee feed data hook instead of regular feed data hook
  const { data, loading, error } = useCommitteeFeedData(feed.apiParams, 1);

  // Only render content if the card is visible
  if (!isVisible) {
    return (
      <div className="card-wrapper">
        <div className="card-loading-container">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-loading">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-error">
          <p style={{ color: 'var(--text-secondary, white)', fontSize: '14px', textAlign: 'center', margin: 0 }}>
            Error loading committee data
          </p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div className="card-wrapper">
        <div className="mobile-card-empty">
          <p style={{ color: 'var(--text-secondary, white)', fontSize: '14px', textAlign: 'center', margin: 0 }}>
            No committee materials available
          </p>
        </div>
      </div>
    );
  }

  // Use your existing FeedItem component with committee-specific schema/table
  return (
    <div className="card-wrapper">
      <div className="card-content" style={{ height: '160px', overflow: 'hidden' }}>
        <FeedItem
          item={data[0]}
          feedName={feed.name}
          branch={feed.feedBranch}
          topic={feed.feedTopic}
          buttonName={feed.buttonName}
          schema="united_states_of_america"
          table="committee_materials"
          expanded={false}
          variant="homepage"
        />
      </div>
    </div>
  );
};

export default MobileFeedCarousel;